import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function QaShippingPage() {
  useSeo({ title: "Shipping Weight QA | Soonyoung", description: "Internal QA: products missing weight/dimensions in the ERP." });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const first = await api.get("/ecom/catalog/products", { params: { page: 1, page_size: 100 } });
        const pages = first.data?.total_pages || 1;
        const collect = (items) => {
          if (cancelled) return;
          setTotal((t) => t + items.length);
          const estimated = items.filter((p) => p.shipping?.weight_is_estimated === true);
          if (estimated.length) setRows((r) => [...(r || []), ...estimated]);
          else setRows((r) => r || []);
        };
        collect(first.data?.items || []);
        await Promise.all(
          Array.from({ length: pages - 1 }, (_, i) =>
            api.get("/ecom/catalog/products", { params: { page: i + 2, page_size: 100 } })
              .then((r) => collect(r.data?.items || []))
              .catch(() => {})
          )
        );
        if (!cancelled) setRows((r) => r || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section data-testid="qa-shipping-page" className="mx-auto max-w-5xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold"><PackageSearch className="h-4 w-4" /> Internal QA</p>
      <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-4xl">Products with estimated shipping weight</h1>
      <p className="mt-3 max-w-2xl text-sm text-sage">
        These products have no Weight / Dimensions in the ERP, so shipping is billed at the 500 g fallback.
        Fill <b className="text-cream">Product Master → Edit Product → "Dimensions &amp; Weight (for shipping)"</b> for each — the API then reflects exact weights automatically.
      </p>

      {rows === null ? (
        <div className="mt-10 space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-moss" />)}</div>
      ) : (
        <>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-gold" data-testid="qa-count">
            {rows.length} products need weight data (of {total} loaded{total < 1 ? "…" : ""})
          </p>
          <ul className="mt-4 divide-y divide-champagne/20 rounded-3xl border border-champagne/40 bg-pine" data-testid="qa-list">
            {rows.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-cream">{p.name}</p>
                  <p className="font-mono text-[10px] text-fern">SKU {p.sku} · ships as {p.shipping?.chargeable_weight_kg} kg (estimated)</p>
                </div>
                <Link to={`/product/${p.slug || p.id}`} className="flex shrink-0 items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gold hover:underline">
                  View <ExternalLink className="h-3 w-3" />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
